/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-case-declarations */
import axios, { CancelTokenSource } from 'axios';
import { SearchResponseHitsContent } from '../../generated/content-exploration';
import { ModifiedTimeOption } from '../components/filters/quickFilter/ModifiedTimeFilter';
import { ConnectedOwnerFilter } from '../components/filters/quickFilter/OwnerFilter';
import { ConnectedModifiedTimeFilter } from '../components/filters/quickFilter/ModifiedTimeFilter';
import { ContentDefinitions } from '../models/contentDefinitions';
import { ContentType } from '../models/ContentType';
import { ContentTypeFilterAllOption, ContentTypeFilterValue } from '../models/ContentTypeFilterValue';
import { FacetResult } from '../models/FacetTypes';
import { SortBy, SortSelection } from '../models/SortSelection';
import { FilterAllOption, FilterQuery } from '../state/FilterState';
import { jsDebugEnabled } from '../../common/utils';
import { Draft } from '../models/SearchResultTypes';

export interface CRASSData {
  drafts?: Draft[];
  totalCount: number;
  list: SearchResponseHitsContent[];
  error?: string;
}

export class CRASSService {
  constructor(private jsDebug: boolean) { }
  public static instance = new CRASSService(jsDebugEnabled());

  private searchCancelTokenSources: { [contentType: string]: CancelTokenSource } = {};
  private getTagListCancelTokenSource: CancelTokenSource = axios.CancelToken.source();
  private getOwnerListCancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  public search(
    contentType: ContentType,
    searchTerm: string,
    startIndex: number,
    pageSize: number,
    filter?: FilterQuery,
    orderBy?: SortSelection
  ): Promise<CRASSData> {
    const cancelTokenSourceKey = [contentType, searchTerm, startIndex, pageSize].join('|');
    if (this.searchCancelTokenSources[cancelTokenSourceKey]) {
      this.searchCancelTokenSources[cancelTokenSourceKey].cancel();
    }

    this.searchCancelTokenSources[cancelTokenSourceKey] = axios.CancelToken.source();

    const page = Math.floor(startIndex / pageSize);
    if ((filter?.owner?.id !== FilterAllOption && !this.supportOwnerFilter(contentType)) || (filter?.modifiedTime?.id !== FilterAllOption && !this.supportModifiedAfterFilter(contentType))) {
      return Promise.resolve({ drafts: [], list: [], totalCount: 0 });
    }

    return new Promise<CRASSData>((resolve, reject) => {
      const headers = {
        accept: 'application/vnd.tableau.search-results.v2+json'
      };
      const promises = [axios.get(this.buildSearchApiUrl(contentType, searchTerm, page, pageSize, filter, orderBy), { headers, cancelToken: this.searchCancelTokenSources[cancelTokenSourceKey].token })];
      if (startIndex % pageSize !== 0) {
        promises.push(axios.get(this.buildSearchApiUrl(contentType, searchTerm, page + 1, pageSize, filter, orderBy), { headers, cancelToken: this.searchCancelTokenSources[cancelTokenSourceKey].token }));
      }

      Promise.all(promises).then((responses) => {
        const result: CRASSData = {
          drafts: responses[0].data.drafts ? responses[0].data.drafts.map((item: any) => { return { parentLuid: item.parentLuid }; }) : [],
          list: [],
          totalCount: responses[0].data.hits.total,
        };
        const offset = startIndex % pageSize;
        result.list = result.list.concat(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          responses[0].data.hits.items ? responses[0].data.hits.items.slice(offset, pageSize).map((item: any) => { return { ...item.content, score: parseFloat(item.score) }; }) : []
        );

        if (responses[1]) {
          result.list = result.list.concat(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            responses[1].data.hits.items ? responses[1].data.hits.items.slice(0, offset).map((item: any) => { return { ...item.content, score: parseFloat(item.score) }; }) : []
          );
        }
        resolve(result);
      }).catch((error => {
        resolve({ drafts: [], list: [], totalCount: 0, error: error.message });
      }));
    })
  }

  public getOwnerList(searchTerm?: string, contentType?: ContentTypeFilterValue, filter?: FilterQuery): Promise<FacetResult[]> {
    this.getOwnerListCancelTokenSource.cancel();
    this.getOwnerListCancelTokenSource = axios.CancelToken.source();

    return new Promise<FacetResult[]>((resolve) => {
      axios.get(this.buildOwnerListApiUrl(searchTerm, contentType, filter), { cancelToken: this.getOwnerListCancelTokenSource.token }).then((response) => {
        const result: FacetResult[] = (response.data.hits.items.map((item: any) => {
          return item.content.ownerId && item.content.ownerName ? {
            value: { name: item.content.ownerName, id: item.content.ownerId.toString() },
            count: item.collapseCount,
          } : null;
        }) as FacetResult[]).filter((item) => item).sort((a, b) => b.count - a.count || a.value.name.localeCompare(b.value.name));

        resolve(result);
      }).catch((error => {
        //do not display error if we cant get owners we just show an empty dropdown
        resolve([]);
      }));
    });
  }

  public getTagList(searchTerm?: string, contentType?: ContentTypeFilterValue): Promise<FacetResult[]> {
    this.getTagListCancelTokenSource.cancel();
    this.getTagListCancelTokenSource = axios.CancelToken.source();

    return new Promise<FacetResult[]>((resolve, reject) => {
      const headers = {
        accept: 'application/vnd.tableau.search-results.v2+json'
      };

      const actualSearchTerm = searchTerm || '';
      const map: { [tag: string]: number } = {};
      const promises: Promise<void>[] = [];

      const chunkCount = 5;

      for (let i = 0; i < chunkCount; i++) {
        ((currentPage) => {
          promises.push(new Promise<void>((resolve) => {
            axios.get(this.buildGetTagApiUrl(actualSearchTerm, contentType || ContentTypeFilterAllOption, currentPage, Math.round(10000 / chunkCount)), { headers, cancelToken: this.getTagListCancelTokenSource.token }).then(response => {
              response.data.hits.items.forEach((item: any) => {
                if (item.content.tags) {
                  item.content.tags.forEach((tag: string) => {
                    if (!map[tag]) {
                      map[tag] = 1;
                    } else {
                      map[tag]++;
                    }
                  });
                }
              });
              resolve();
            })
              .catch(() => {
                resolve();
              });
          }));
        })(i);
      }

      const convertMapToFacetResults = () => {
        const result: FacetResult[] = [];
        Object.keys(map).forEach(key => {
          result.push({ value: { name: key, id: key }, count: map[key] });
        });
        return result;
      };

      Promise.all(promises).then(() => {
        resolve(convertMapToFacetResults());
      }).catch(() => {
        resolve(convertMapToFacetResults());
      });
    });
  }

  private buildSearchApiUrl(
    contentType: ContentType,
    searchTerm: string,
    startIndex: number,
    pageSize: number,
    filter?: FilterQuery,
    orderBy?: SortSelection
  ): string {
    const baseUrl = '/content-exploration/search?';
    const params: { name: string; value: string | number }[] = [
      { name: 'page', value: startIndex },
      { name: 'limit', value: pageSize },
    ];

    if (this.jsDebug) {
      params.push({ name: 'includeDiagnostics', value: 'true' })
    }

    if (searchTerm) {
      params.push({ name: 'terms', value: encodeURIComponent(searchTerm) });
    }

    const filterValue = [this.handleContentTypeFilter(contentType), this.handlerOwnerFilter(filter), this.handleModifiedAfterFilter(filter), this.handleTagFilter(filter)]
      .filter(item => item)
      .join(',');

    params.push({ name: 'filter', value: filterValue });

    // relevancy is the default, so no additional arguments are needed to CRASS
    if (orderBy && orderBy.name !== SortBy.getRelevancySortSelection().name) {
      const order = !orderBy.secondarySort
        ? `${orderBy.primarySort.value}:${orderBy.primarySortDirection}`
        : `${orderBy.primarySort.value}:${orderBy.primarySortDirection},${orderBy.secondarySort.value}:${orderBy.secondarySortDirection}`;
      params.push({ name: 'order_by', value: encodeURI(order) });
    }
    return baseUrl + params.map((item) => item.name + '=' + item.value).join('&');
  }

  private buildGetTagApiUrl(
    searchTerm: string,
    contentType: ContentTypeFilterValue,
    page: number,
    pageSize: number,
  ): string {
    const baseUrl = '/content-exploration/search?';
    const params: { name: string; value: string | number }[] = [
      { name: 'page', value: page },
      { name: 'limit', value: pageSize },
    ];

    if (this.jsDebug) {
      params.push({ name: 'includeDiagnostics', value: 'true' })
    }

    if (searchTerm) {
      params.push({ name: 'terms', value: encodeURIComponent(searchTerm) });
    }

    if (contentType !== ContentTypeFilterAllOption) {
      params.push({ name: 'filter', value: this.handleContentTypesFilter(contentType) });
    }

    params.push({ name: 'fields', value: 'tags' });

    return baseUrl + params.map((item) => item.name + '=' + item.value).join('&');
  }

  private handleContentTypeFilter(contentType: ContentType) {
    const filterPublishedDatasource = contentType === ContentType.unifieddatasource ? ',datasourceIsPublished:eq:true' : '';
    return 'type:eq:' + contentType + filterPublishedDatasource;
  }

  private buildOwnerListApiUrl(searchTerm?: string, contentType?: ContentTypeFilterValue, filter?: FilterQuery) {
    const baseUrl = '/content-exploration/facets?';
    const params: { name: string; value: string | number }[] = [];

    params.push({ name: 'facet', value: 'ownerId' });
    if (searchTerm) {
      params.push({ name: 'terms', value: encodeURIComponent(searchTerm) });
    }
    if (contentType) {
      const filterValue = [this.handleContentTypesFilter(contentType), this.handleModifiedAfterFilter(filter)]
        .filter(item => item)
        .join(',');

      params.push({ name: 'filter', value: filterValue });
    }

    return baseUrl + params.map((item) => item.name + '=' + item.value).join('&');
  }

  private handleContentTypesFilter(contentType: ContentTypeFilterValue) {
    const filterValue = contentType === ContentTypeFilterAllOption
      ? Object.keys(ContentType)
        .filter((c) => this.supportOwnerFilter(c as ContentType))
        .join(',')
      : contentType;

    return 'type:in:[' + filterValue + ']';
  }

  private handlerOwnerFilter(filter?: FilterQuery) {
    return filter?.owner && filter.owner.id !== FilterAllOption ? 'ownerId:eq:' + filter.owner.id : '';
  }

  private handleTagFilter(filter?: FilterQuery) {
    const result = filter?.tag && filter.tag.id !== FilterAllOption ? 'tags:in:[' + window.encodeURIComponent(filter.tag.id) + ']' : '';
    return result;
  }

  private handleModifiedAfterFilter(filter?: FilterQuery) {
    if (filter?.modifiedTime && filter.modifiedTime.id !== FilterAllOption) {
      let timestamp = '';
      const now = this.getNow();
      const day = 24 * 60 * 60 * 1000;

      const getTargetTimestamp = (dayOffset: number) => {
        const targetDate = new Date(now.getTime() - dayOffset * day);
        targetDate.setHours(0);
        targetDate.setMinutes(0);
        targetDate.setSeconds(0);
        targetDate.setMilliseconds(0);
        return targetDate.toISOString();
      };

      switch (filter.modifiedTime.id) {
        case ModifiedTimeOption.today:
          timestamp = getTargetTimestamp(0);
          break;
        case ModifiedTimeOption.yesterday:
          timestamp = getTargetTimestamp(1);
          break;
        case ModifiedTimeOption.last7days:
          timestamp = getTargetTimestamp(6); // Since today is included, so just set offset to 7 minus 1. Same for the offsets below.
          break;
        case ModifiedTimeOption.last30days:
          timestamp = getTargetTimestamp(29);
          break;
        case ModifiedTimeOption.last90days:
          timestamp = getTargetTimestamp(89);
          break;
      }
      return 'modifiedTime:gte:' + timestamp;
    }

    return '';
  }

  private supportOwnerFilter(contentType: ContentType) {
    return ContentDefinitions[contentType].quickFilters?.indexOf(ConnectedOwnerFilter) !== -1 && ContentDefinitions[contentType].shouldBeAvailable();
  }

  private supportModifiedAfterFilter(contentType: ContentType) {
    return ContentDefinitions[contentType].quickFilters?.indexOf(ConnectedModifiedTimeFilter) !== -1;
  }

  public getNow() {
    // This function is to allow the test code to mock the "getNow" behavior.
    return new Date();
  }
}
